import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Link,
  Container,
  Button,
  IconButton
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useUserType } from '../../contexts/UserTypeContext';
import { getUserProfile } from '../../services/supabaseService';
import type { UserProfile } from '../../services/supabaseService';
import { Navigate, useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import { ProfileSection } from '../../components/mypage/ProfileSection';
import { NotificationsOutlined, ChevronRight, Menu } from '@mui/icons-material';
import AdvertiserDrawer from '../../components/drawers/AdvertiserDrawer';

const AdvertiserMyPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { currentType, setCurrentType } = useUserType();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        if (currentUser?.uid) {
          const userProfile = await getUserProfile(currentUser.uid);
          setProfile(userProfile);
        }
      } catch (err) {
        console.error('Error loading data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [currentUser]);

  if (!currentUser) {
    return <Navigate to="/login" state={{ from: '/mypage' }} />;
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  const campaignStats = {
    대기중: 0,
    모집중: 0,
    선정발표: 0,
    등록기간: 0,
    종료: 0
  };

  return (
    <>
      <Header showIcons={true} />
      <Box sx={{ bgcolor: '#0066FF', color: 'white', py: 3, borderRadius: 2 }}>
        <Container maxWidth="md">
          <ProfileSection
            profile={profile}
          />
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ mt: 3 }}>
        <Box sx={{ mb: 4 }}>
          <Typography 
            component="h2" 
            sx={{ 
              fontSize: '1rem', 
              fontWeight: 500,
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <span>내 광고 현황</span>
            <Button
              variant="text"
              size="small"
              onClick={() => navigate('/campaign/create')}
              sx={{
                color: '#0066FF',
                fontSize: '0.875rem',
                '&:hover': {
                  background: 'none',
                  opacity: 0.8
                }
              }}
            >
              캠페인 등록 &gt;
            </Button>
          </Typography>
          <Paper 
            elevation={0} 
            sx={{ 
              bgcolor: '#F8F9FA',
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            <Box sx={{ 
              display: 'grid',
              gridTemplateColumns: `repeat(${Object.keys(campaignStats).length}, 1fr)`,
              '& > *': {
                borderRight: '1px solid #eee',
                '&:last-child': {
                  borderRight: 'none'
                }
              }
            }}>
              {Object.entries(campaignStats).map(([status, count]) => (
                <Box 
                  key={status} 
                  sx={{ 
                    textAlign: 'center',
                    py: 2
                  }}
                >
                  <Typography 
                    color="text.secondary" 
                    variant="body2" 
                    sx={{ 
                      mb: 0.5, 
                      fontSize: '0.875rem',
                      color: 'rgba(0, 0, 0, 0.6)'
                    }}
                  >
                    {status}
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 500,
                      color: '#0066FF',
                      fontSize: '1.25rem'
                    }}
                  >
                    {count}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Box>

        {/* Notifications */}
        <Box>
          <Paper 
            elevation={0}
            sx={{ 
              p: 2,
              bgcolor: '#F8F9FA',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <NotificationsOutlined sx={{ mr: 1, color: 'text.secondary', fontSize: '1.25rem' }} />
              <Typography sx={{ fontSize: '0.875rem' }}>
                알림 0개
              </Typography>
            </Box>
            <Link 
              href="/notifications"
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                color: '#0066FF',
                fontSize: '0.875rem',
                '&:hover': { 
                  textDecoration: 'none',
                  opacity: 0.8
                }
              }}
            >
              전체알림
              <ChevronRight sx={{ ml: 0.5, fontSize: '1rem' }} />
            </Link>
          </Paper>
        </Box>
      </Container>

      {/* Advertiser Drawer */}
      <AdvertiserDrawer 
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />
    </>
  );
};

export default AdvertiserMyPage; 